import { useEffect, useRef, useState } from "react";
import { WidgetDataState } from "../models/Dashboard";
function evictStaleTokens<TData>(
    cache: Map<string, TData>,
    baseKey: string,
    keepKey: string
): void {
    for (const existingKey of [...cache.keys()]) {
        if (existingKey === keepKey) continue;
        // Re-derive each entry's base (drop its last element) and compare.
        try {
            const parsed = JSON.parse(existingKey) as unknown[];
            const existingBase = JSON.stringify(parsed.slice(0, -1));
            if (existingBase === baseKey) {
                cache.delete(existingKey);
            }
        } catch {
            // Non-JSON / malformed key — leave it alone.
        }
    }
}


const useFetchWidgetData = <TData,>({
  key,
  fetch,
  onError,
  useCache = true,
}: {
  key: (string | boolean | number)[];
  fetch: () => Promise<TData>;
  onError?: (error: Error) => void;
  useCache?: boolean;
}): WidgetDataState<TData> => {
  const cacheKey = JSON.stringify(key);

    const baseKey = JSON.stringify(key.slice(0, -1));

  const [state, setState] = useState<WidgetDataState<TData>>({
    status: "loading",
    data: null,
    error: null,
  });

  const cacheRef = useRef<Map<string, TData>>(new Map());
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (useCache && cacheRef.current.has(cacheKey)) {
      const cachedData = cacheRef.current.get(cacheKey)!;
      const isEmpty =
        cachedData == null ||
        (Array.isArray(cachedData) && (cachedData as unknown[]).length === 0);
      setState(
        isEmpty
          ? { status: "empty", data: null, error: null }
          : { status: "success", data: cachedData, error: null }
      );
      return;
    }

    let cancelled = false;
    setState({ status: "loading", data: null, error: null });

    fetchRef.current()
      .then((data) => {
        if (cancelled) {
          return;
        }
        if (useCache) {
            evictStaleTokens(cacheRef.current, baseKey, cacheKey);
            cacheRef.current.set(cacheKey, data);
        }

        const isEmpty =
          data == null ||
          (Array.isArray(data) && (data as unknown[]).length === 0);
        setState(
          isEmpty
            ? { status: "empty", data: null, error: null }
            : { status: "success", data, error: null }
        );
      })
      .catch((error: Error) => {
        if (cancelled) return;
        setState({ status: "error", data: null, error });
        onErrorRef.current?.(error);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, baseKey ,useCache]);

  return state;
};

export default useFetchWidgetData;
